import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { PageHeaderComponent } from '../../../../shared/components/page-header/page-header.component';
import { SkeletonLoaderComponent } from '../../../../shared/components/skeleton/skeleton.component';
import { VideoModalComponent } from '../../../../shared/components/video-modal/video-modal.component';
import { DEFAULT_FILTER } from '../../../../shared/constants/constants';
import { StorageKeys, UserRoles } from '../../../../shared/enums/enums';
import { FilterModel } from '../../../../shared/interface/interface';
import { VideoModel } from '../../../../shared/models/video.model';
import { StorageService } from '../../../../shared/services/storage.service';
import { VideoService } from '../../../../shared/services/video.service';
import { SharedModule } from '../../../../shared/shared.module';
import { VideoCardComponent } from '../video-card/video-card.component';

@Component({
  selector: 'app-videos',
  standalone: true,
  templateUrl: './videos.component.html',
  styleUrl: './videos.component.scss',
  imports: [
    PageHeaderComponent,
    VideoCardComponent,
    SharedModule,
    ConfirmationModalComponent,
    SkeletonLoaderComponent,
    VideoModalComponent,
  ],
})
export class VideosComponent implements OnInit, OnDestroy {
  private videoFilterSubject$: BehaviorSubject<FilterModel> =
    new BehaviorSubject<FilterModel>(DEFAULT_FILTER);
  videoFilter = this.videoFilterSubject$.asObservable();
  subscription = new Subscription();
  videos: VideoModel[] = [];
  videosCount = 0;
  videoListLoading = false;
  menuOpenIndex = -1;
  deleteVideoId!: string | undefined;
  deleteLoading = false;
  selectedVideo: VideoModel | null = null;
  isAdmin = false;

  constructor(
    private videoService: VideoService,
    private toastr: ToastrService,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.init();
  }

  init() {
    this.subscribeToSubject();
    this.isAdmin =
      this.storageService.getItem(StorageKeys.userRole) === UserRoles.ADMIN;
  }

  subscribeToSubject() {
    const sub = this.videoFilter.subscribe({
      next: (filter) => {
        this.getVideos(filter);
      },
      error: () => {},
    });
    this.subscription.add(sub);
  }

  getVideos(filter: FilterModel) {
    this.videoListLoading = true;
    this.videoService.fetchVideos(filter).subscribe({
      next: (res) => {
        this.videos =
          filter.pageNumber > 1 ? [...this.videos, ...res.result] : res.result;
        this.videosCount = res.count;
        this.videoListLoading = false;
      },
      error: (err) => {
        this.toastr.error(
          err?.error?.message || err?.message || 'Something went wrong!'
        );
        this.videoListLoading = false;
      },
    });
  }

  closeAllMenu(index: number) {
    if (index === this.menuOpenIndex) {
      this.menuOpenIndex = -1;
    }
  }
  openMenu(index: number) {
    this.menuOpenIndex = index;
  }

  loadMoreIfLastIndex() {
    const { searchText, pageNumber, limit } = this.videoFilterSubject$.value;
    if (pageNumber * limit < this.videosCount) {
      this.updateVideoFilter({
        searchText,
        limit,
        pageNumber: pageNumber + 1,
      });
    }
  }
  updateVideoFilter(filter: FilterModel) {
    this.videoFilterSubject$.next(filter);
  }
  cancelDelete() {
    this.deleteVideoId = undefined;
  }
  deleteVideo() {
    if (this.deleteVideoId) {
      this.deleteLoading = true;
      this.videoService.deleteVideo(this.deleteVideoId).subscribe({
        next: (res) => {
          this.deleteLoading = false;
          this.deleteVideoId = undefined;
          this.updateVideoFilter(DEFAULT_FILTER);
          this.toastr.success('Video deleted successfully.');
        },
        error: (err) => {
          console.error(err);
          this.toastr.error(
            err?.error?.message || err?.message || 'Something went wrong!'
          );
          this.deleteLoading = false;
        },
      });
    }
  }
  onDeleteVideo(id: string) {
    this.deleteVideoId = id;
  }
  handleSearch(searchText: string) {
    if (searchText.length === 0 || searchText?.trim()) {
      this.updateVideoFilter({
        ...this.videoFilterSubject$.value,
        pageNumber: 1,
        searchText: searchText,
      });
    }
  }

  handleVideoClose() {
    this.selectedVideo = null;
  }

  selectVideo(video: VideoModel) {
    this.selectedVideo = video;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
